import { motion } from 'framer-motion'

const partners = [
  {
    name: 'Vibe Ten',
    role: 'Yoga',
    position: { top: '4%', left: '6%', rotate: -3 },
  },
  {
    name: 'Fit Factory',
    role: 'Group Strength Training',
    position: { top: '26%', right: '6%', rotate: 4 },
  },
  {
    name: 'F45 Yonge & Eglinton',
    role: 'Functional Group Training',
    position: { bottom: '6%', left: '24%', rotate: 2 },
  },
]

const WovenThread = () => (
  <svg
    className="absolute inset-0 w-full h-full pointer-events-none"
    viewBox="0 0 600 600"
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    <defs>
      <linearGradient id="threadGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#c54819" stopOpacity="0.35" />
        <stop offset="50%" stopColor="#a38568" stopOpacity="0.45" />
        <stop offset="100%" stopColor="#1D7000" stopOpacity="0.35" />
      </linearGradient>
    </defs>
    {/* Organic hand-drawn path weaving through the partner nodes */}
    <motion.path
      d="M 90,80 C 200,40 340,180 510,120 C 580,90 560,300 430,360 C 280,420 150,340 100,470 C 70,540 280,560 520,540"
      fill="none"
      stroke="url(#threadGradient)"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeDasharray="4 6"
      initial={{ pathLength: 0 }}
      whileInView={{ pathLength: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 3, ease: 'easeInOut' }}
    />
  </svg>
)

const Sparkle = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M12 2v20M2 12h20" />
  </svg>
)

const Partnerships = () => {
  return (
    <section className="relative bg-cream-100 py-20 md:py-28 overflow-hidden">
      {/* Subtle dotted grain — continues the paper/textured language */}
      <div
        className="absolute inset-0 opacity-50 pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(109,42,20,0.08) 1px, transparent 0)',
          backgroundSize: '18px 18px',
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-12 lg:gap-16 items-center">
          {/* Left — editorial copy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="relative"
          >
            <span className="font-script text-brand-orange-500 italic text-lg mb-3 block">
              Our Community Partners
            </span>
            <h2 className="font-display text-dark-700 text-3xl md:text-4xl lg:text-5xl font-semibold leading-[1.1] mb-6">
              A network of care, close to home.
            </h2>
            <p className="font-sans text-dark-500 text-base md:text-lg leading-relaxed">
              Recovery is rarely one-size-fits-all. That&apos;s why we
              collaborate with local practitioners and businesses whose
              expertise complements our own, helping you access the right
              support when you need it.
            </p>

            {/* Decorative corner sparkle */}
            <Sparkle className="hidden md:block absolute -top-4 -left-6 w-5 h-5 text-brand-orange-500/40" />
          </motion.div>

          {/* Right — scattered partner plates with woven thread */}
          <div className="relative min-h-[460px] md:min-h-[520px] lg:min-h-[560px]">
            <WovenThread />

            {/* Ambient sparkles scattered in the background */}
            <Sparkle className="absolute top-[14%] left-[42%] w-4 h-4 text-dark-400/40" />
            <Sparkle className="absolute top-[48%] right-[28%] w-3 h-3 text-brand-green-600/40" />
            <Sparkle className="absolute bottom-[16%] left-[42%] w-4 h-4 text-brand-orange-500/40" />

            {partners.map((partner, index) => (
              <motion.div
                key={partner.name}
                className="absolute"
                style={partner.position}
                initial={{ opacity: 0, scale: 0.85, rotate: 0 }}
                whileInView={{
                  opacity: 1,
                  scale: 1,
                  rotate: partner.position.rotate,
                }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{
                  duration: 0.7,
                  delay: 0.25 + index * 0.15,
                  ease: 'easeOut',
                }}
                whileHover={{
                  rotate: 0,
                  scale: 1.04,
                  transition: { duration: 0.3 },
                }}
              >
                {/* Circular seal / plate */}
                <div className="relative w-40 h-40 md:w-48 md:h-48 rounded-full bg-cream-50 border border-dark-300/50 shadow-[0_8px_24px_-12px_rgba(47,40,34,0.25)] flex flex-col items-center justify-center px-5 text-center cursor-default transition-shadow duration-300 hover:shadow-[0_12px_32px_-12px_rgba(47,40,34,0.35)]">
                  {/* Inner hairline ring for the "seal" detail */}
                  <div className="absolute inset-[6px] rounded-full border border-dark-300/30 pointer-events-none" />

                  <span className="font-script italic text-brand-orange-500 text-xs tracking-wide mb-1">
                    partner
                  </span>
                  <h3 className="font-display text-dark-700 text-lg md:text-xl font-semibold leading-tight mb-1.5">
                    {partner.name}
                  </h3>
                  <p className="font-sans text-dark-500 text-[11px] md:text-xs tracking-[0.18em] uppercase">
                    {partner.role}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default Partnerships
